import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState, SortingState } from '@tanstack/react-table'
import { Download, FileText, Plus, Send } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { aFechaISO, formatearFecha } from '@/lib/fecha'
import { formatearRNC } from '@/lib/rnc'
import { descargarArchivo } from '@/api/cliente'
import { Boton } from '@/ui/Boton'
import { useAvisos } from '@/ui/Avisos'
import { Banner } from '@/ui/Banner'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { LineaDeTiempo, type EventoLinea } from '@/ui/LineaDeTiempo'
import { ENCF, FechaCorta, FechaHora, Monto, RNC } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import type { Tono } from '@/ui/tonos'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import { useFiltrosEnUrl } from '@/ui/tabla/filtrosEnUrl'
import { Dato, EncabezadoPantalla } from './-pantalla'
import { BotonWhatsAppDocumento } from './-cuentas-por-cobrar/BotonWhatsAppDocumento'
import { PRESENTACION_ESTADO, bordeDeFila } from '@/dominio/documentos/estados'
import { traducirErrorApi, traducirRechazo } from '@/dominio/documentos/errores'
import {
  TIPOS_ECF,
  nombreTipoECF,
  type Documento,
  type EstadoDocumento,
  type EventoDocumento,
  type TipoECF,
} from '@/dominio/documentos/tipos'
import { useDocumento, useDocumentos, useUbicaciones } from '@/dominio/documentos/useDocumentos'
import {
  TEXTO_ACCION,
  etiquetaCanal,
  nombreReceptor,
  presentacionDeEntrega,
  salidaMotivoAcuse,
  sePuedeReintentar,
  textoAcuse,
  textoMotivoAcuse,
} from '@/dominio/entregas/tipos'
import { useEntregasDeDocumento, useReintentarEntrega } from '@/dominio/entregas/useEntregas'

/**
 * Listado y detalle de documentos (E34).
 *
 * Lo que decide si esta pantalla está bien:
 *
 *   **`aceptado_condicional` es un estado VÁLIDO.** Se muestra en ámbar con la
 *   palabra "Válido" al lado, distinto de `rechazado` (rojo, con borde
 *   izquierdo y el motivo en segunda línea) y de `aceptado` (verde). Pintarlo
 *   como error genera notas de crédito innecesarias, que es el error más común
 *   del dominio
 *
 *   los filtros viven en la URL, así que la vista se comparte por enlace
 *
 *   la fila de un rechazado muestra el motivo **sin tener que entrar**
 */
/**
 * Los filtros viven en la URL, y **todos son opcionales**: un enlace a
 * `/documentos` sin filtros tiene que seguir siendo un enlace válido, y un
 * esquema con claves obligatorias obligaría a cada enlace del producto a
 * enumerarlas todas.
 */
export interface BusquedaDocumentos {
  estado?: string
  tipo?: string
  desde?: string
  hasta?: string
  texto?: string
  /** Código de la sucursal donde se vendió (lo manda la caja). */
  sucursal?: string
  /** Código de la caja que vendió. */
  caja?: string
  /** El documento abierto en el panel. En la URL para que sea enlazable. */
  documento?: string
}

export const Route = createFileRoute('/documentos')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaDocumentos => ({
    estado: typeof busqueda.estado === 'string' ? busqueda.estado : undefined,
    tipo: typeof busqueda.tipo === 'string' ? busqueda.tipo : undefined,
    desde: typeof busqueda.desde === 'string' ? busqueda.desde : undefined,
    hasta: typeof busqueda.hasta === 'string' ? busqueda.hasta : undefined,
    texto: typeof busqueda.texto === 'string' ? busqueda.texto : undefined,
    sucursal: typeof busqueda.sucursal === 'string' ? busqueda.sucursal : undefined,
    caja: typeof busqueda.caja === 'string' ? busqueda.caja : undefined,
    documento: typeof busqueda.documento === 'string' ? busqueda.documento : undefined,
  }),
  component: PantallaDocumentos,
})

const ESTADOS_FILTRABLES: readonly EstadoDocumento[] = [
  'aceptado',
  'aceptado_condicional',
  'en_proceso',
  'rechazado',
  'rechazado_corregible',
  'encolado',
  'en_contingencia',
  'anulado',
]

function PantallaDocumentos() {
  const navegar = useNavigate({ from: Route.fullPath })
  const busqueda = Route.useSearch()
  const { filtros, aplicar, limpiar, hayFiltrosActivos } = useFiltrosEnUrl({
    estado: '',
    tipo: '',
    desde: '',
    hasta: '',
    texto: '',
    sucursal: '',
    caja: '',
  })

  const [pagina, setPagina] = useState(1)
  const [tamano, setTamano] = useState(50)
  const [orden, setOrden] = useState<SortingState>([{ id: 'fecha', desc: true }])
  const [seleccion, setSeleccion] = useState<RowSelectionState>({})

  const consulta = useDocumentos(
    {
      estado: filtros.estado === '' ? undefined : (filtros.estado as EstadoDocumento),
      tipo: filtros.tipo === '' ? undefined : (filtros.tipo as TipoECF),
      desde: filtros.desde === '' ? undefined : filtros.desde,
      hasta: filtros.hasta === '' ? undefined : filtros.hasta,
      texto: filtros.texto === '' ? undefined : filtros.texto,
      sucursal: filtros.sucursal === '' ? undefined : filtros.sucursal,
      caja: filtros.caja === '' ? undefined : filtros.caja,
    },
    pagina,
    tamano,
  )

  // Con qué llenar los filtros de sucursal y caja: lo que aparece en el período filtrado, o en los
  // últimos noventa días si no hay período. Un contribuyente sin caja no ve ni los selectores.
  const ventana = useMemo(() => {
    const hoy = new Date()
    return {
      desde: filtros.desde !== '' ? filtros.desde : aFechaISO(new Date(hoy.getTime() - 90 * 86_400_000)),
      hasta: filtros.hasta !== '' ? filtros.hasta : aFechaISO(hoy),
    }
  }, [filtros.desde, filtros.hasta])
  const ubicaciones = useUbicaciones(ventana.desde, ventana.hasta)
  const sucursales = ubicaciones.data?.sucursales ?? []
  const cajas = (ubicaciones.data?.cajas ?? []).filter(
    (c) => filtros.sucursal === '' || c.sucursal === filtros.sucursal,
  )
  const conUbicaciones =
    (consulta.data?.filas ?? []).some((f) => f.sucursal != null || f.caja != null) ||
    sucursales.length > 0 ||
    cajas.length > 0

  const abrirDetalle = (id: string | undefined): void => {
    void navegar({ search: (previo) => ({ ...previo, documento: id }), replace: false })
  }

  const columnas = useMemo<ColumnDef<Documento, unknown>[]>(
    () => [
      {
        id: 'fecha',
        accessorFn: (fila) => fila.fecha,
        header: 'Fecha',
        meta: { etiqueta: 'Fecha', numerico: true, ancho: '100px', exportar: (f) => formatearFecha(f.fecha) },
        cell: ({ row }) => <FechaCorta valor={row.original.fecha} />,
      },
      {
        id: 'encf',
        accessorFn: (fila) => fila.eNCF ?? '',
        header: 'e-NCF',
        meta: { etiqueta: 'e-NCF', numerico: true, ancho: '165px', exportar: (f) => f.eNCF },
        cell: ({ row }) => <ENCF valor={row.original.eNCF} />,
      },
      {
        id: 'tipo',
        accessorFn: (fila) => fila.tipo,
        header: 'Tipo',
        meta: { etiqueta: 'Tipo', ancho: '160px', exportar: (f) => `${f.tipo} ${nombreTipoECF(f.tipo)}` },
        cell: ({ row }) => (
          <span className="flex items-baseline gap-2">
            <span className="mono text-gris-600">{row.original.tipo}</span>
            <span className="truncate text-gris-900">{nombreTipoECF(row.original.tipo)}</span>
          </span>
        ),
      },
      {
        id: 'contraparte',
        accessorFn: (fila) => fila.contraparteNombre ?? '',
        header: 'Cliente',
        meta: { etiqueta: 'Cliente', exportar: (f) => f.contraparteNombre },
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            {/*
              La razón social puede faltar: el consumidor final de un tipo 32 no
              la tiene. Se enseña el RNC, que sí está, en vez de un hueco.
            */}
            <span
              className="truncate text-gris-900"
              title={row.original.contraparteNombre ?? undefined}
            >
              {row.original.contraparteNombre ?? formatearRNC(row.original.contraparteRNC)}
            </span>
          </span>
        ),
      },
      {
        id: 'rnc',
        accessorFn: (fila) => fila.contraparteRNC ?? '',
        header: 'RNC',
        meta: { etiqueta: 'RNC', numerico: true, ancho: '130px', exportar: (f) => formatearRNC(f.contraparteRNC) },
        cell: ({ row }) => <RNC valor={row.original.contraparteRNC} />,
      },
      ...(conUbicaciones ? [columnaUbicacion] : []),
      {
        id: 'total',
        accessorFn: (fila) => fila.total,
        header: 'Monto',
        meta: { etiqueta: 'Monto', numerico: true, ancho: '130px', exportar: (f) => f.total },
        cell: ({ row }) => <Monto valor={row.original.total} moneda={row.original.moneda} />,
      },
      {
        id: 'estado',
        accessorFn: (fila) => fila.estado,
        header: 'Estado',
        enableSorting: false,
        meta: {
          etiqueta: 'Estado',
          ancho: '225px',
          exportar: (f) => PRESENTACION_ESTADO[f.estado].etiqueta,
        },
        cell: ({ row }) => {
          const presentacion = PRESENTACION_ESTADO[row.original.estado]
          return (
            // El aceptado condicional sale en ámbar con "Válido" al lado. Es
            // válido y el usuario no debe alarmarse.
            <InsigniaEstado
              tono={presentacion.tono}
              aclaracion={presentacion.aclaracion}
              titulo={presentacion.descripcion}
            >
              {presentacion.etiqueta}
            </InsigniaEstado>
          )
        },
      },
    ],
    [conUbicaciones],
  )

  const errorTraducido = consulta.isError ? traducirErrorApi(consulta.error) : null

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Documentos"
        acciones={
          <Link to="/emitir">
            <Boton variante="primario" icono={<Plus />} atajo="N">
              Nueva factura
            </Boton>
          </Link>
        }
      />

      <TablaDatos<Documento>
        idTabla="documentos"
        columnas={columnas}
        datos={consulta.data?.filas ?? []}
        obtenerId={(fila) => fila.id}
        cargando={consulta.isLoading}
        error={
          errorTraducido === null
            ? null
            : {
                titulo: errorTraducido.titulo,
                contexto: errorTraducido.contexto,
                detalleTecnico: errorTraducido.detalleTecnico,
              }
        }
        onReintentar={() => void consulta.refetch()}
        vacio={{
          variante: 'primera-vez',
          mensaje: 'Aún no has emitido facturas.',
          accion: (
            <Link to="/emitir">
              <Boton variante="primario" icono={<Plus />}>
                Nueva factura
              </Boton>
            </Link>
          ),
        }}
        vacioFiltrado={{
          variante: 'sin-resultados',
          mensaje: describirFiltroVacio(filtros),
          accion: (
            <Boton variante="secundario" tamano="menor" onClick={limpiar}>
              Limpiar filtros
            </Boton>
          ),
        }}
        orden={orden}
        onOrdenChange={setOrden}
        seleccionable
        seleccion={seleccion}
        onSeleccionChange={setSeleccion}
        accionesSeleccion={(ids) => (
          <>
            <Boton variante="secundario" tamano="menor" icono={<Download />} disabled>
              Descargar {ids.length} en lote
            </Boton>
            <Boton variante="secundario" tamano="menor" icono={<Send />} disabled>
              Reenviar por correo
            </Boton>
          </>
        )}
        paginacion={{ pagina, tamano, total: consulta.data?.total ?? 0 }}
        onPaginacionChange={(siguiente) => {
          setPagina(siguiente.pagina)
          setTamano(siguiente.tamano)
        }}
        nombreExportacion="documentos"
        hayFiltrosActivos={hayFiltrosActivos}
        onLimpiarFiltros={limpiar}
        filtros={
          <>
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Estado
              <select
                value={filtros.estado}
                onChange={(evento) => aplicar({ estado: evento.target.value })}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {ESTADOS_FILTRABLES.map((estado) => (
                  <option key={estado} value={estado}>
                    {PRESENTACION_ESTADO[estado].etiqueta}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Tipo
              <select
                value={filtros.tipo}
                onChange={(evento) => aplicar({ tipo: evento.target.value })}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {(Object.keys(TIPOS_ECF) as TipoECF[]).map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo} · {TIPOS_ECF[tipo]}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Desde
              <input
                type="date"
                value={filtros.desde}
                onChange={(evento) => aplicar({ desde: evento.target.value })}
                className="numero h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-gris-900"
              />
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Hasta
              <input
                type="date"
                value={filtros.hasta}
                onChange={(evento) => aplicar({ hasta: evento.target.value })}
                className="numero h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-gris-900"
              />
            </label>

            {conUbicaciones || filtros.sucursal !== '' ? (
              <label className="flex items-center gap-1 text-etiqueta text-gris-600">
                Sucursal
                <select
                  value={filtros.sucursal}
                  // Al cambiar de sucursal la caja elegida deja de ser suya: se suelta.
                  onChange={(evento) => aplicar({ sucursal: evento.target.value, caja: '' })}
                  className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
                >
                  <option value="">Todas</option>
                  {conOpcionActual(sucursales, filtros.sucursal).map((s) => (
                    <option key={s.codigo} value={s.codigo}>
                      {nombreUbicacion(s)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {cajas.length > 0 || filtros.caja !== '' ? (
              <label className="flex items-center gap-1 text-etiqueta text-gris-600">
                Caja
                <select
                  value={filtros.caja}
                  onChange={(evento) => aplicar({ caja: evento.target.value })}
                  className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
                >
                  <option value="">Todas</option>
                  {conOpcionActual(cajas, filtros.caja).map((c) => (
                    <option key={`${c.sucursal ?? ''}·${c.codigo}`} value={c.codigo}>
                      {nombreUbicacion(c)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <input
              type="search"
              value={filtros.texto}
              onChange={(evento) => aplicar({ texto: evento.target.value })}
              placeholder="Cliente, RNC o e-NCF"
              aria-label="Buscar en los documentos"
              className="h-[var(--alto-control-menor)] w-[220px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
            />
          </>
        }
        onFilaClick={(fila) => abrirDetalle(fila.id)}
        segundaLinea={(fila) =>
          fila.motivoRechazo !== null &&
          (fila.estado === 'rechazado' || fila.estado === 'rechazado_corregible') ? (
            <span className="text-rojo" title={fila.motivoRechazo}>
              {fila.motivoRechazo}
            </span>
          ) : null
        }
        bordeFila={(fila) => bordeDeFila(fila.estado)}
      />

      <PanelDeDocumento id={busqueda.documento ?? null} onCerrar={() => abrirDetalle(undefined)} />
    </div>
  )
}

/**
 * La columna de dónde se vendió. Solo aparece cuando hay comprobantes de caja: a quien emite desde
 * el portal le sobraría una columna vacía.
 */
const columnaUbicacion: ColumnDef<Documento, unknown> = {
  id: 'sucursal',
  accessorFn: (fila) => fila.sucursal?.codigo ?? '',
  header: 'Sucursal · caja',
  meta: {
    etiqueta: 'Sucursal · caja',
    ancho: '170px',
    exportar: (f) => [f.sucursal?.codigo, f.caja?.codigo].filter(Boolean).join(' · '),
  },
  cell: ({ row }) => {
    const { sucursal, caja } = row.original
    if (sucursal == null && caja == null) return <span className="text-gris-500">—</span>
    return (
      <span className="truncate text-gris-900" title={[sucursal?.nombre, caja?.nombre].filter(Boolean).join(' · ')}>
        {sucursal ? nombreUbicacion(sucursal) : '—'}
        {caja ? <span className="text-gris-600"> · {nombreUbicacion(caja)}</span> : null}
      </span>
    )
  },
}

function nombreUbicacion(u: { codigo: string; nombre: string | null }): string {
  return u.nombre ? `${u.nombre} (${u.codigo})` : u.codigo
}

/**
 * Las opciones del selector, con la elegida aunque no aparezca en la ventana: un enlace con
 * `?sucursal=S04` de hace un año tiene que poder leerse, y quitarse.
 */
function conOpcionActual<T extends { codigo: string; nombre: string | null }>(
  opciones: T[],
  actual: string,
): { codigo: string; nombre: string | null; sucursal?: string | null }[] {
  return actual === '' || opciones.some((o) => o.codigo === actual)
    ? opciones
    : [{ codigo: actual, nombre: null }, ...opciones]
}

function describirFiltroVacio(filtros: { desde: string; hasta: string; estado: string }): string {
  if (filtros.desde !== '' && filtros.hasta !== '') {
    return `Ninguna factura entre el ${formatearFecha(filtros.desde)} y el ${formatearFecha(filtros.hasta)}.`
  }
  if (filtros.estado !== '') {
    const presentacion = PRESENTACION_ESTADO[filtros.estado as EstadoDocumento]
    return `Ninguna factura en estado ${presentacion?.etiqueta.toLowerCase() ?? filtros.estado}.`
  }
  return 'Ninguna factura con los filtros aplicados.'
}

/* ---------------------------------------------------------------------- */
/* Detalle                                                                 */
/* ---------------------------------------------------------------------- */

/**
 * Detalle en panel lateral: **el listado queda visible detrás**, que es el
 * punto de usar un panel y no una página nueva.
 *
 * Lo que este panel leía y el servidor nunca mandó: `subtotalGravado`,
 * `exento`, `totalItbis`, `saldo`, `trackId`, `origen`, `causaRechazo` y
 * `secuenciaUtilizada`. Ninguno reventaba —el formateador devuelve «—» ante un
 * `undefined`—, y por eso llevaban ahí desde el cambio de origen: tres filas de
 * totales en guion sobre un documento con cifras reales, que es peor que un
 * error porque parece un documento sin impuestos.
 * `RespuestaDetalleDocumento` manda `total`, `itbis`, `totalDop`, las líneas ya
 * calculadas, las referencias y los eventos. Se enseña eso.
 */
function PanelDeDocumento({ id, onCerrar }: { id: string | null; onCerrar: () => void }) {
  const consulta = useDocumento(id)
  const documento = consulta.data
  const { publicar } = useAvisos()
  const [bajando, setBajando] = useState<'pdf' | 'xml' | null>(null)

  // Los extremos existen desde la emisión propia (`/interna/documentos/{id}/pdf` y `/xml`); los
  // botones seguían deshabilitados. Sin e-NCF no hay comprobante que bajar.
  const bajar = async (que: 'pdf' | 'xml'): Promise<void> => {
    if (id === null) return
    setBajando(que)
    try {
      await descargarArchivo(`/documentos/${encodeURIComponent(id)}/${que}`)
    } catch (error) {
      publicar({
        tono: 'rojo',
        titulo: que === 'pdf' ? 'No se pudo bajar el PDF' : 'No se pudo bajar el XML',
        detalle: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setBajando(null)
    }
  }

  const eventos = useMemo<EventoLinea[]>(() => {
    if (documento === undefined) return []
    return documento.eventos.map((evento) => ({
      id: evento.id,
      // El servidor no manda una etiqueta redactada: manda la transición. La
      // etiqueta sale de la misma tabla que usa el listado, para que el mismo
      // estado no se llame de dos maneras en dos sitios de la misma pantalla.
      etiqueta: etiquetaDelEvento(evento),
      momento: evento.momento,
      // Ahí viven el track_id, el estado_dgii y el secuencia_utilizada. Va
      // plegado: el usuario normal no lo abre nunca y soporte lo necesita
      // siempre.
      crudo: evento.detalle ?? undefined,
      tono: evento.estadoNuevo === null ? 'neutral' : tonoDelEstado(evento.estadoNuevo),
    }))
  }, [documento])

  const rechazo =
    documento?.estado === 'rechazado' || documento?.estado === 'rechazado_corregible'
      ? traducirRechazo(
          // El backend todavía no normaliza la causa en un código propio, así
          // que se traduce por el caso general y el texto de DGII viaja como
          // detalle técnico. Inventar una causa aquí sería peor que no tenerla:
          // cada una propone una salida distinta, y la equivocada manda al
          // usuario a arreglar algo que no está roto.
          'desconocido',
          { nombreContraparte: documento.contraparteNombre ?? undefined, tipo: documento.tipo },
          documento.motivoRechazo ?? undefined,
        )
      : null

  const presentacion = documento ? PRESENTACION_ESTADO[documento.estado] : null

  return (
    <PanelLateral
      abierto={id !== null}
      onCerrar={onCerrar}
      ancho="ancho"
      titulo={documento?.contraparteNombre ?? 'Documento'}
      subtitulo={
        documento ? (
          <span className="flex items-center gap-2">
            <ENCF valor={documento.eNCF} />
            <span>·</span>
            <span>{nombreTipoECF(documento.tipo)}</span>
          </span>
        ) : undefined
      }
      pie={
        <>
          <Boton
            variante="secundario"
            icono={<FileText />}
            disabled={!documento?.eNCF || bajando !== null}
            onClick={() => void bajar('pdf')}
          >
            Descargar PDF
          </Boton>
          <Boton
            variante="secundario"
            icono={<Download />}
            disabled={!documento?.eNCF || bajando !== null}
            onClick={() => void bajar('xml')}
          >
            Descargar XML
          </Boton>
          <Boton variante="secundario" disabled>
            Emitir nota de crédito
          </Boton>
          {documento ? <BotonWhatsAppDocumento documento={documento} /> : null}
        </>
      }
    >
      {consulta.isLoading || documento === undefined || presentacion === null ? (
        <div className="flex flex-col gap-2">
          <LineaEsqueleto ancho="w-2/3" />
          <LineaEsqueleto ancho="w-1/2" />
          <LineaEsqueleto ancho="w-5/6" />
          <LineaEsqueleto ancho="w-1/3" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/*
            Un rechazo se traduce: qué pasó, por qué, y el botón que lleva donde
            se arregla. Ningún mensaje crudo de DGII llega al usuario; el crudo
            queda plegado, más abajo, en la línea de tiempo.
          */}
          {rechazo !== null ? (
            <Banner
              tono="rojo"
              titulo={rechazo.titulo}
              accion={
                rechazo.accion ? (
                  <Link to={rechazo.accion.destino}>
                    <Boton variante="secundario" tamano="menor">
                      {rechazo.accion.etiqueta}
                    </Boton>
                  </Link>
                ) : undefined
              }
            >
              {rechazo.contexto}
              {/*
                Un rechazo no siempre quema la secuencia: lo decide la pareja
                (código 2, secuenciaUtilizada), y `false` significa que el e-NCF
                SÍ se puede reutilizar. El backend ya resuelve esa pareja en el
                propio estado —`rechazado_corregible` es justo ese caso—, así
                que se lee de ahí y no de un campo suelto que nadie manda.
              */}
              {documento.estado === 'rechazado_corregible' ? (
                <p className="pt-1 text-etiqueta text-gris-600">
                  El e-NCF <span className="mono">{documento.eNCF}</span> no se quemó: se puede
                  reutilizar al corregir.
                </p>
              ) : null}
            </Banner>
          ) : null}

          {documento.estado === 'aceptado_condicional' ? (
            // Ámbar, no rojo, y lo dice con todas las letras: emitir una nota
            // de crédito por esto es el error más común del dominio.
            <Banner tono="ambar" titulo="Aceptada. La factura es válida.">
              DGII registró una observación menor de tolerancia. No hace falta nota de crédito.
            </Banner>
          ) : null}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]">
            {/* Izquierda: el documento tal como se ve ------------------- */}
            <div className="flex min-w-0 flex-col gap-4">
              <dl className="flex flex-col">
                <Dato etiqueta="Estado">
                  <InsigniaEstado
                    tono={presentacion.tono}
                    aclaracion={presentacion.aclaracion}
                    titulo={presentacion.descripcion}
                  >
                    {presentacion.etiqueta}
                  </InsigniaEstado>
                </Dato>
                <Dato etiqueta="Fecha">
                  <FechaCorta valor={documento.fecha} />
                </Dato>
                <Dato etiqueta="RNC">
                  <RNC valor={documento.contraparteRNC} />
                </Dato>
                {documento.sucursal != null ? (
                  <Dato etiqueta="Sucursal">
                    <span className="text-gris-900">{nombreUbicacion(documento.sucursal)}</span>
                  </Dato>
                ) : null}
                {documento.caja != null ? (
                  <Dato etiqueta="Caja">
                    <span className="text-gris-900">{nombreUbicacion(documento.caja)}</span>
                  </Dato>
                ) : null}
                {documento.tasaCambio !== null ? (
                  <Dato etiqueta="Tasa de cambio">
                    <span className="numero text-gris-700">{documento.tasaCambio}</span>
                  </Dato>
                ) : null}
                {documento.anuladoEl !== null ? (
                  <Dato etiqueta="Anulado el">
                    <FechaCorta valor={documento.anuladoEl} />
                  </Dato>
                ) : null}
                {documento.motorEmision !== null ? (
                  // Qué motor lo emitió explica una diferencia de formato entre
                  // dos comprobantes del mismo tenant: es la primera pregunta de
                  // soporte el día que el adaptador externo y el motor propio
                  // conviven en la misma cartera.
                  <Dato etiqueta="Motor de emisión">
                    <InsigniaDiscreta>{documento.motorEmision}</InsigniaDiscreta>
                  </Dato>
                ) : null}
                <Dato etiqueta="Correlación">
                  <span className="mono text-etiqueta text-gris-600">{documento.correlacion}</span>
                </Dato>
              </dl>

              <div data-densidad="compacta">
                <table className="w-full table-fixed border-collapse text-cuerpo">
                  <thead className="bg-gris-50">
                    <tr className="border-b border-gris-300">
                      <th scope="col" className="h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600">
                        Descripción
                      </th>
                      <th scope="col" className="h-[var(--alto-fila)] w-[64px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                        Cant
                      </th>
                      <th scope="col" className="h-[var(--alto-fila)] w-[100px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                        Precio
                      </th>
                      <th scope="col" className="h-[var(--alto-fila)] w-[92px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                        ITBIS
                      </th>
                      <th scope="col" className="h-[var(--alto-fila)] w-[104px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                        Monto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documento.lineas.map((linea) => (
                      <tr key={linea.numero} className="h-[var(--alto-fila)] border-b border-gris-200">
                        <td className="overflow-hidden px-2">
                          <span className="block truncate text-gris-900" title={linea.descripcion}>
                            {/* Solo la letra E a la izquierda cuando es exento. */}
                            {linea.indicador === '4' ? <span className="mono text-gris-600">E </span> : null}
                            {linea.descripcion}
                          </span>
                        </td>
                        <td className="numero px-2 text-gris-700">{linea.cantidad}</td>
                        <td className="px-2 text-right">
                          <Monto valor={linea.precio} decimales={2} />
                        </td>
                        <td className="px-2 text-right">
                          <Monto valor={linea.itbis} />
                        </td>
                        <td className="px-2 text-right">
                          <Monto valor={linea.monto} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <dl className="flex flex-col">
                <Dato etiqueta="Total ITBIS">
                  <Monto valor={documento.itbis} moneda={documento.moneda} />
                </Dato>
                <Dato etiqueta="Total">
                  <Monto
                    valor={documento.total}
                    moneda={documento.moneda}
                    mostrarMoneda
                    className="text-gris-900"
                  />
                </Dato>
                {documento.moneda !== 'DOP' ? (
                  // La conversión con la tasa congelada al emitir. Es la cifra
                  // que entra en el 607, y no se recalcula al mirarla.
                  <Dato etiqueta="Total en pesos">
                    <Monto valor={documento.totalDop} mostrarMoneda />
                  </Dato>
                ) : null}
              </dl>

              {documento.referencias.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <h3 className="text-seccion text-gris-900">Referencias</h3>
                  {/* Una nota de crédito parcial no anula el original: le reduce
                      el saldo. El saldo vivo no viaja todavía en el contrato, así
                      que lo que se enseña es el vínculo con el comprobante
                      modificado y el monto afectado, que sí. */}
                  <ul className="flex flex-col">
                    {documento.referencias.map((referencia) => (
                      <li
                        key={referencia.encfReferido}
                        className="flex flex-wrap items-baseline gap-2 border-b border-gris-200 py-1 last:border-b-0"
                      >
                        <ENCF valor={referencia.encfReferido} />
                        <span className="text-etiqueta text-gris-600">{referencia.tipoModificacion}</span>
                        {referencia.montoAfectado !== null ? (
                          <Monto valor={referencia.montoAfectado} className="ml-auto" />
                        ) : null}
                        {referencia.razon !== null ? (
                          <span className="w-full text-etiqueta text-gris-600">{referencia.razon}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            {/* Derecha: su vida ---------------------------------------- */}
            <div className="flex min-w-0 flex-col gap-5">
              <div className="min-w-0">
                <h3 className="pb-2 text-seccion text-gris-900">Su vida</h3>
                <LineaDeTiempo eventos={eventos} />
              </div>

              <EntregaAlComprador
                id={documento.id}
                // La factura de consumo sin comprador no se encola nunca: es el consumidor
                // final del mostrador, sin RNC, y lo que le toca es la representación impresa.
                // El servidor aplica la misma regla en el cierre del documento.
                sinComprador={documento.tipo === '32' && documento.contraparteRNC === null}
              />
            </div>
          </div>
        </div>
      )}
    </PanelLateral>
  )
}

/**
 * La etiqueta de un evento.
 *
 * Se prefiere el nombre del estado al que llegó, que es el vocabulario que el
 * usuario ya conoce del listado. Los eventos que no mueven el estado —el
 * `creado`, el `numerado`, el `firmado`— caen en su propio tipo.
 */
function etiquetaDelEvento(evento: EventoDocumento): string {
  if (evento.estadoNuevo !== null && evento.estadoNuevo !== evento.estadoAnterior) {
    return PRESENTACION_ESTADO[evento.estadoNuevo].etiqueta
  }
  return evento.tipo.replace(/_/g, ' ')
}

function tonoDelEstado(estado: EstadoDocumento): Tono {
  return PRESENTACION_ESTADO[estado].tono
}

/**
 * Si el comprobante le llegó al comprador, que es una obligación distinta de
 * haberlo transmitido a DGII.
 *
 * **Sin esta sección un documento "Aceptado" parece terminado y puede no
 * haberse entregado nunca.** La transmisión y la entrega son dos deberes del
 * emisor electrónico y sus desenlaces son independientes: DGII puede haberlo
 * aceptado mientras el servicio del comprador estaba caído.
 *
 * `no_entregable` sale en gris y con su explicación. Es el desenlace correcto
 * del comprador que no es receptor electrónico —el caso mayoritario del
 * mercado, todo consumidor final— y pintarlo como problema haría que el usuario
 * aprendiera a ignorar esta sección, que es justo lo contrario de para lo que
 * está.
 */
function EntregaAlComprador({ id, sinComprador }: { id: string; sinComprador: boolean }) {
  const consulta = useEntregasDeDocumento(id)
  const reintentar = useReintentarEntrega()

  const ultimo = consulta.data?.[0]

  return (
    <div className="min-w-0">
      <h3 className="pb-2 text-seccion text-gris-900">Entrega al comprador</h3>

      {consulta.isPending ? <LineaEsqueleto /> : null}

      {consulta.isError ? (
        // El traductor devuelve el hecho y su contexto por separado; aquí cabe
        // una línea, así que se usa el contexto, que es el que dice qué hacer.
        <p className="text-etiqueta text-gris-600">
          No se pudo leer el estado de la entrega. {traducirErrorApi(consulta.error).contexto}
        </p>
      ) : null}

      {consulta.isSuccess && ultimo === undefined ? (
        sinComprador ? (
          // No es «todavía»: esta no se va a intentar nunca, y decir que sí haría
          // esperar al usuario algo que no llega. No es un fallo, es el caso normal
          // del mostrador.
          <p className="text-etiqueta text-gris-600">
            Factura de consumo sin comprador identificado: no hay a quién entregarle el
            XML. Le corresponde la representación impresa.
          </p>
        ) : (
          // Ni un intento todavía. No es un fallo: la entrega se encola cuando
          // DGII acepta el comprobante, así que antes de eso no hay nada que ver.
          <p className="text-etiqueta text-gris-600">
            Todavía no se ha intentado. Se entrega sola cuando DGII acepte el comprobante.
          </p>
        )
      ) : null}

      {ultimo !== undefined ? (
        <EstadoDeEntrega
          fila={ultimo}
          intentos={consulta.data?.length ?? 1}
          onReintentar={() => reintentar.mutate(id)}
          reintentando={reintentar.isPending}
          respuesta={reintentar.data ?? null}
        />
      ) : null}
    </div>
  )
}

function EstadoDeEntrega({
  fila,
  intentos,
  onReintentar,
  reintentando,
  respuesta,
}: {
  fila: NonNullable<ReturnType<typeof useEntregasDeDocumento>['data']>[number]
  intentos: number
  onReintentar: () => void
  reintentando: boolean
  respuesta: { encolada: boolean; motivo: string | null } | null
}) {
  const presentacion = presentacionDeEntrega(fila.estado)
  const acuse = textoAcuse(fila.acuseEstado)
  const motivo = textoMotivoAcuse(fila.acuseMotivo)
  const salida = salidaMotivoAcuse(fila.acuseMotivo)

  return (
    <div className="flex flex-col gap-2">
      <InsigniaEstado tono={presentacion.tono} aclaracion={presentacion.aclaracion}>
        {presentacion.etiqueta}
      </InsigniaEstado>

      <p className="text-etiqueta text-gris-600">{presentacion.explicacion}</p>

      <dl className="flex flex-col">
        <Dato etiqueta="Receptor">{nombreReceptor(fila)}</Dato>
        <Dato etiqueta="Canal">{etiquetaCanal(fila.canal)}</Dato>
        {acuse !== null ? (
          <Dato etiqueta="Acuse del receptor">
            {acuse}
            {motivo !== null ? <span className="text-gris-600"> · {motivo}</span> : null}
          </Dato>
        ) : null}
        {fila.entregadoEl !== null ? (
          <Dato etiqueta="Entregado">
            <FechaHora valor={fila.entregadoEl} />
          </Dato>
        ) : null}
        {intentos > 1 ? <Dato etiqueta="Intentos">{intentos}</Dato> : null}
      </dl>

      {/* Qué hacer con esto, en la misma tarjeta. Un estado sin salida obliga
          al usuario a deducirla, y las dos que se confunden —corregir y
          reintentar— llevan a sitios opuestos. */}
      <p className="text-etiqueta text-gris-600">{salida ?? TEXTO_ACCION[presentacion.accion]}</p>

      {fila.mensaje !== null && fila.estado !== 'no_entregable' ? (
        <p className="text-etiqueta text-gris-600">
          {fila.httpStatus !== null ? `HTTP ${fila.httpStatus} · ` : ''}
          {fila.mensaje}
        </p>
      ) : null}

      {sePuedeReintentar(fila) ? (
        <div className="flex items-center gap-2">
          <Boton variante="secundario" onClick={onReintentar} disabled={reintentando}>
            {reintentando ? 'Encolando…' : 'Reintentar la entrega'}
          </Boton>
        </div>
      ) : null}

      {/* Que no se encole no es un error de la petición: es una respuesta, y
          hay que decirla. Callarla deja al usuario pulsando un botón que no
          hace nada visible. */}
      {respuesta !== null && !respuesta.encolada && respuesta.motivo !== null ? (
        <p className="text-etiqueta text-ambar-700">{respuesta.motivo}</p>
      ) : null}

      <Link
        to="/entregas"
        search={{ texto: fila.eNCF ?? undefined }}
        className="text-etiqueta text-azul-700 underline-offset-2 hover:underline"
      >
        Ver el historial completo de entregas
      </Link>
    </div>
  )
}
